import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { NavigationBar, type ViewType } from '../components/Navigation/NavigationBar'
import { ScheduleList } from '@components/schedule/ScheduleList'
import { NowNextLater } from '@components/schedule/NowNextLater'
import { TimelineView } from '@components/schedule/TimelineView'
import { ChatInterface } from '@components/chat/ChatInterface'
import { DataSourcePanel } from '../components/ingestion/DataSourcePanel'
import { ToastNotification } from '@shared/components'
import { LocationDashboard } from '../components/LocationTracking/LocationDashboard'
import { ComplianceDashboard } from '../components/Compliance/ComplianceDashboard'
import { SuggestionsDashboard } from '../components/suggestions/SuggestionsDashboard'
import { SuggestionModal } from '../components/suggestions/SuggestionModal'
import { SuppliersDirectory } from '../components/suppliers/SuppliersDirectory'
import { mockWeddingEvent, mockMessages, mockDataSourceStatus, mockSuggestions } from '../data/mockWeddingData'
import { mockSupplierLocations, mockComplianceData } from '../data/mockLocationData'
import { mockSuggestions as enhancedMockSuggestions } from '../data/mockSuggestionsData'
import { rescheduleTask, validateTaskMove } from '../utils/scheduleUtils'
import type { Task, Event, Message, Suggestion } from '../types/mockup'
import '../styles/mockup.css'

const ScheduleMockupFixed: React.FC = () => {
  const [event, setEvent] = useState<Event>(mockWeddingEvent)
  const [selectedTask, setSelectedTask] = useState<Task | undefined>()
  const [messages] = useState<Message[]>(mockMessages)
  const [activeView, setActiveView] = useState<ViewType>('schedule')
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'warning' | 'error' }>>([])
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null)
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)

  const currentTime = useMemo(() => {
    return new Date('2024-12-15T14:30:00')
  }, [])

  // Calculate badges
  const navigationBadges = useMemo(() => ({
    messages: messages.filter(m => !m.isProcessed).length || undefined,
    suggestions: enhancedMockSuggestions.filter(s => s.status === 'pending').length || undefined,
    tracking: mockSupplierLocations.filter(s => s.status === 'wrong-site').length > 0 
      ? { wrongSite: mockSupplierLocations.filter(s => s.status === 'wrong-site').length }
      : undefined,
    compliance: mockComplianceData.filter(s => s.overallStatus === 'non-compliant').length > 0
      ? { issues: mockComplianceData.filter(s => s.overallStatus === 'non-compliant').length }
      : undefined
  }), [messages])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        switch(e.key) {
          case '1': e.preventDefault(); setActiveView('schedule'); break;
          case '2': e.preventDefault(); setActiveView('timeline'); break;
          case '3': e.preventDefault(); setActiveView('messages'); break;
          case '4': e.preventDefault(); setActiveView('tracking'); break;
          case '5': e.preventDefault(); setActiveView('suggestions'); break;
          case '6': e.preventDefault(); setActiveView('data'); break;
          case '7': e.preventDefault(); setActiveView('compliance'); break;
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleTaskUpdate = useCallback((taskId: string, updates: Partial<Task>) => {
    setEvent(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t)
    }))
  }, [])

  const handleTaskDrop = useCallback((draggedTaskId: string, targetTaskId: string) => {
    const draggedTask = event.tasks.find(t => t.id === draggedTaskId)
    const targetTask = event.tasks.find(t => t.id === targetTaskId)
    
    if (!draggedTask || !targetTask) return
    
    const validation = validateTaskMove(draggedTask, targetTask, event.tasks)
    if (!validation.valid) {
      setToasts(prev => [...prev, {
        id: Date.now().toString(),
        message: validation.reason || 'Cannot move this task',
        type: 'error'
      }])
      return
    }
    
    const updatedTasks = rescheduleTask(draggedTask, targetTask, event.tasks)
    setEvent(prev => ({ ...prev, tasks: updatedTasks }))
    
    setToasts(prev => [...prev, {
      id: Date.now().toString(),
      message: `Moved "${draggedTask.title}" successfully`,
      type: 'success'
    }])
  }, [event.tasks])

  const renderMainContent = () => {
    switch(activeView) {
      case 'schedule':
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px 16px 24px',
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              color: 'white',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  📅
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Event Schedule</h3>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Real-time task management & coordination</div>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
              {/* Now/Next/Later Widget */}
              <NowNextLater 
                tasks={event.tasks} 
                currentTime={currentTime}
              />
              
              {/* Schedule List */}
              <ScheduleList
                tasks={event.tasks}
                suppliers={event.suppliers}
                onTaskClick={setSelectedTask}
                selectedTaskId={selectedTask?.id}
                onTaskUpdate={handleTaskUpdate}
                onTaskDrop={handleTaskDrop}
                groupBySupplier={false}
                showCompleted={true}
                currentTime={currentTime}
              />
            </div>
          </div>
        )

      case 'timeline':
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px 16px 24px',
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
              color: 'white',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  ⏰
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Event Timeline</h3>
                  <div style={{ fontSize: '14px', opacity: 0.9 }}>Visual schedule with progress tracking</div>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
              <TimelineView
                tasks={event.tasks}
                currentTime={currentTime}
                onTaskClick={setSelectedTask}
                onTaskStatusChange={handleTaskUpdate}
              />
            </div>
          </div>
        )

      case 'messages':
        return (
          <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <ChatInterface
              event={event}
              onCommandExecute={(command, result) => {
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: `Command executed: ${command}`,
                  type: 'success'
                }])
              }}
              isProcessing={false}
            />
          </div>
        )

      case 'tracking':
        return (
          <div style={{ padding: '20px' }}>
            <LocationDashboard 
              suppliers={mockSupplierLocations}
              onAction={(supplierId, action) => {
                const supplier = mockSupplierLocations.find(s => s.id === supplierId)
                const messages: { [key: string]: string } = {
                  'directions': `Directions sent to ${supplier?.name}`,
                  'request-location': `Location request sent to ${supplier?.name}`,
                  'call': `Calling ${supplier?.name}...`
                }
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: messages[action] || 'Action completed',
                  type: 'success'
                }])
              }}
            />
          </div>
        )

      case 'compliance':
        return (
          <div style={{ padding: '20px' }}>
            <ComplianceDashboard
              suppliers={mockComplianceData.map(s => ({
                ...s,
                completionRate: s.overallStatus === 'compliant' ? 100 : 
                               s.overallStatus === 'partial' ? 75 : 25,
                requirements: s.requirements.map(r => ({
                  ...r,
                  category: r.name.toLowerCase().includes('insurance') ? 'insurance' as const :
                           r.name.toLowerCase().includes('safety') ? 'safety' as const :
                           r.name.toLowerCase().includes('certificate') || r.name.toLowerCase().includes('cert') ? 'certification' as const :
                           'other' as const
                }))
              }))}
              onRequestDocuments={(supplierId) => {
                const supplier = mockComplianceData.find(s => s.id === supplierId)
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: `Document request sent to ${supplier?.name}`,
                  type: 'success'
                }])
              }}
              onExportPack={(supplierId) => {
                const supplier = mockComplianceData.find(s => s.id === supplierId)
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: `Compliance pack exported for ${supplier?.name}`,
                  type: 'success'
                }])
              }}
              onExportAll={() => {
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: 'Full compliance report exported',
                  type: 'success'
                }])
              }}
            />
          </div>
        )

      case 'suggestions':
        return (
          <>
            <SuggestionsDashboard
              initialSuggestions={enhancedMockSuggestions}
              onViewDetails={(suggestion) => {
                setSelectedSuggestion(suggestion)
                setShowSuggestionModal(true)
              }}
              onQuickApply={(suggestionId) => {
                const suggestion = enhancedMockSuggestions.find(s => s.id === suggestionId)
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: `Applied: ${suggestion?.headline}`,
                  type: 'success'
                }])
              }}
              onQuickDismiss={(suggestionId) => {
                const suggestion = enhancedMockSuggestions.find(s => s.id === suggestionId)
                setToasts(prev => [...prev, {
                  id: Date.now().toString(),
                  message: `Dismissed: ${suggestion?.headline}`,
                  type: 'warning'
                }])
              }}
            />
            
            {selectedSuggestion && (
              <SuggestionModal
                suggestion={selectedSuggestion}
                isOpen={showSuggestionModal}
                onClose={() => {
                  setShowSuggestionModal(false)
                  setSelectedSuggestion(null)
                }}
                onApply={(suggestionId) => {
                  setToasts(prev => [...prev, {
                    id: Date.now().toString(),
                    message: `Applied suggestion: ${selectedSuggestion.headline}`,
                    type: 'success'
                  }])
                  setShowSuggestionModal(false)
                  setSelectedSuggestion(null)
                }}
                onDismiss={(suggestionId, reason) => {
                  setToasts(prev => [...prev, {
                    id: Date.now().toString(),
                    message: `Dismissed suggestion: ${reason}`,
                    type: 'warning'
                  }])
                  setShowSuggestionModal(false)
                  setSelectedSuggestion(null)
                }}
              />
            )}
          </>
        )
        
      case 'suggestions-old':
        return (
          <div style={{ padding: '20px' }}>
            <h2>AI Suggestions (Old View)</h2>
            <div style={{ marginTop: '20px' }}>
              {mockSuggestions.map((suggestion, index) => (
                <div key={index} style={{
                  padding: '16px',
                  marginBottom: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e8eaed'
                }}>
                  <h3>{suggestion.title}</h3>
                  <p>{suggestion.description}</p>
                  <div style={{ marginTop: '8px' }}>
                    <button style={{
                      padding: '6px 12px',
                      backgroundColor: '#1a73e8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      marginRight: '8px'
                    }}>
                      Apply
                    </button>
                    <button style={{
                      padding: '6px 12px',
                      backgroundColor: '#fff',
                      color: '#5f6368',
                      border: '1px solid #dadce0',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}>
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case 'data':
        return (
          <div style={{ padding: '20px' }}>
            <DataSourcePanel 
              messages={messages}
              dataSourceStatus={mockDataSourceStatus}
              onMessageClick={(msg) => console.log('Message clicked:', msg)}
              roleView="coordinator"
              simulateMessages={false}
              event={event}
            />
          </div>
        )

      case 'notifications':
        return (
          <div style={{ padding: '20px' }}>
            <h2>Notifications Center</h2>
            <p>Recent notifications will appear here</p>
          </div>
        )

      case 'suppliers':
        return <SuppliersDirectory />

      case 'settings':
        return (
          <div style={{ padding: '20px' }}>
            <h2>Event Settings</h2>
            <div style={{ marginTop: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h3>Anchor Tasks</h3>
                <p style={{ color: '#5f6368' }}>These tasks cannot be moved</p>
                {event.tasks.filter(t => t.isAnchor).map(task => (
                  <div key={task.id} style={{ padding: '8px', backgroundColor: '#f8f9fa', marginBottom: '4px', borderRadius: '4px' }}>
                    {task.title} - {task.startTime}
                  </div>
                ))}
              </div>
              <div>
                <h3>Buffer Time</h3>
                <p style={{ color: '#5f6368' }}>Global buffer: 5 minutes</p>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Navigation Bar */}
      <NavigationBar 
        activeView={activeView}
        onViewChange={setActiveView}
        badges={navigationBadges}
      />

      {/* Main Content */}
      <main style={{ backgroundColor: '#fff', minHeight: 'calc(100vh - 120px)' }}>
        {renderMainContent()}
      </main>

      {/* Toast Notifications */}
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          id={toast.id}
          title={toast.type === 'success' ? 'Success' : toast.type === 'error' ? 'Error' : 'Notice'}
          message={toast.message}
          type={toast.type}
          duration={5000}
          onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
        />
      ))}
    </div>
  )
}

export default ScheduleMockupFixed